import { Trans, useTranslation } from "react-i18next";
import { SPOTButton } from "spot-component-library/lib/components/SPOTButton";
import { Panel } from "spot-component-library/lib/components/basic/panel/Panel";
import { SPOTNotification } from "spot-component-library/lib/components/notification/SPOTNotification";
import { PaymentFormData } from "../../types/PaymentFormData";
import { PaymentType } from "../../types/PaymentType";
import { RateAgreementOptions } from "../../types/RateAgreementOptions";
import { RateData } from "../../types/RateData";
import "./RatePlanSummary.scss";

import { format } from "date-fns";
import FormatNumber, { formattedNumber } from "../../components/FormatNumber";
import { mapPaymentMethod } from "../../utils/paymentMethods";
import { useCurrency } from "../../contexts/TenantContext";
import { useEffect } from "react";
import { useSettlementFee } from "../../contexts/SettlementFeeContext";

type PaymentProps = {
    paymentForm: PaymentFormData;
    rateAgreementOptions: RateAgreementOptions;
    debtBalance: number;
    rateData: RateData;
    onBack: (paymentForm: PaymentFormData) => void;
    onNext: () => void;
    errorMessage?: string;
    showSpinner: boolean;
    debtNumber?: string;
};

export const RatePlanSummary = (props: PaymentProps) => {
    const { t } = useTranslation();
    const currency = useCurrency();
    const settlementFee = useSettlementFee();

    const showSpinner = settlementFee.requestIsPending || props.showSpinner;

    useEffect(() => {
        if (settlementFee.isInitialized) {
            settlementFee.update(props.rateData);
        }
    }, [settlementFee.isInitialized]);

    return (
        <>
            <div data-test="ratePlan" id={"ratePlan"}>
                <Panel>
                    <div className={"ratePlan"}>
                        <p>
                            <b>{t("SUMMARY_PAGE.OVERVIEW_TITLE")}</b>
                        </p>
                    </div>

                    <div className={"whiteBlock"} data-test="rateDataSummaryBlock">
                        <div>
                            <div className={"plan-summary-wrapper"}>
                                <div className={"row-wrapper"}>
                                    <div>{t("SUMMARY_PAGE.MonthlyRate")}</div>
                                    <b>
                                        <FormatNumber number={props.rateData.rateAmount} currency={currency} dataTest={"rateAmount"} />
                                    </b>
                                </div>
                                <div className={"row-wrapper"}>
                                    <div>{t("SUMMARY_PAGE.AnzahlDerRaten")}</div>
                                    <b>
                                        <span data-test="rateNumber">{props.rateData.rateNumber}</span>
                                    </b>
                                </div>
                                <div className={"row-wrapper"}>
                                    <div>{t("SUMMARY_PAGE.DatumErsteZahlung")}</div>
                                    <b>
                                        <span data-test="firstDateOfPay">{props.rateData.dateOfFirstPay ? format(props.rateData.dateOfFirstPay, "dd.MM.yyyy") : ""}</span>
                                    </b>
                                </div>
                                <div className={"row-wrapper"}>
                                    <div>{t("SUMMARY_PAGE.PAYMENT_INTERVAL")}</div>
                                    <b>
                                        <span data-test="rateCycle">{t("SUMMARY_PAGE.MONTHLY")}</span>
                                    </b>
                                </div>
                                <div className={"row-wrapper"}>
                                    <div>{t("SUMMARY_PAGE.PaymentType")}</div>
                                    <b>
                                        <span data-test="paymentType">{t(mapPaymentMethod(props.paymentForm.paymentType))}</span>
                                    </b>
                                </div>
                            </div>
                        </div>

                        {props.paymentForm.paymentType === PaymentType.ABO_BANKTRANSFER && (
                            <>
                                <div className={"summary-bank-transfer-title"}>{t("SUMMARY_PAGE.BANK_TRANSFER_TITLE")}</div>

                                <div className={"summary-bank-transfer-option-block"}>
                                    <div className={"row-wrapper"}>
                                        <span>{t("SUMMARY_PAGE.BANK_TRANSFER_ACCOUNT_HOLDER")}</span>
                                        <span id={"account-holder"}>{props.paymentForm.kontoinhaber}</span>
                                    </div>
                                    <div className={"row-wrapper"}>
                                        <span>{t("SUMMARY_PAGE.BANK_TRANSFER_BANK")}</span>
                                        <span id={"bank-name"}>{props.paymentForm.kreditinstitut}</span>
                                    </div>
                                    <div className={"row-wrapper"}>
                                        <span>{t("SUMMARY_PAGE.BANK_TRANSFER_IBAN")}</span>
                                        <span id={"iban"}>{props.paymentForm.iban}</span>
                                    </div>
                                    <div className={"row-wrapper"}>
                                        <span>{t("SUMMARY_PAGE.BANK_TRANSFER_PAYMENT_REFERENCE")}</span>
                                        <span id={"bank-transfer-reference"}>
                                            {t("SUMMARY_PAGE.BANK_TRANSFER_PAYMENT_REFERENCE_PREFIX")}
                                            {props.rateData.debtNumber}
                                        </span>
                                    </div>
                                </div>
                            </>
                        )}

                        {settlementFee.value && settlementFee.value > 0 && (
                            <p data-test="settlementFee" data-value={settlementFee.value}>
                                <Trans
                                    i18nKey="SUMMARY_PAGE.SETTLEMENT_FEE_NOTE"
                                    values={{
                                        settlementFeeWithBalance: formattedNumber(settlementFee.value + props.debtBalance),
                                        settlementFee: formattedNumber(settlementFee.value),
                                        debtNo: props.rateData.debtNumber,
                                    }}
                                />
                            </p>
                        )}

                        {settlementFee.requestHasFailed && <SPOTNotification active text={t("APP_ROOT.ERROR.SERVER_MESSAGE_DEFAULT")} type="error" />}
                    </div>

                    {props.errorMessage && <SPOTNotification active text={props.errorMessage} type={"error"} />}
                </Panel>
            </div>
            <div className={"buttonBlock"}>
                <SPOTButton
                    id={"buttonBackToPaymentForm"}
                    type="secondary"
                    label={t("SUMMARY_PAGE.Zurueck")}
                    onClick={() => {
                        props.onBack(props.paymentForm);
                    }}
                />
                <SPOTButton
                    id={"buttonBackToSuccess"}
                    type="primary"
                    label={t("SUMMARY_PAGE.finish")}
                    onClick={() => props.onNext()}
                    disabled={showSpinner || settlementFee.requestHasFailed}
                    isLoading={showSpinner}
                />
            </div>
        </>
    );
};
